class CartRecommendations {
  constructor() {
    this.cartRecommendationsContainer = null;
    this.cartItemsContainer = null;
    this.originalRecommendations = [];
    this.originalRecommendationProductIds = new Set();
    this.dynamicRecommendations = [];
    this.currentCartProductIds = new Set();
    this.previousCartProductIds = new Set();
    this.removedProductsData = new Map();
    this.addedFromRecommendations = new Set();
    this.isInitialized = false;
    this.isCartDrawerOpen = false;
    this.cartOriginType = null; // 'au-local', 'eu-local', 'international', or null (empty cart)
    this.marketType = null; // 'australia', 'europe', or 'international'
    this.isAustralianMarket = false;
    this.isEuropeanMarket = false;
    this.enableTailored = false;
    this.hasRestrictionCollection = false;
    this.restrictionCollectionHandle = null;
    this.restrictedProductIds = new Set();
    this.cartHasPreorderProducts = false; // Track if cart contains pre-order products
    
    this.setupCartDrawerListeners();
  }

  setupCartDrawerListeners() {
    document.addEventListener('cart:open', () => {
      this.onCartDrawerOpen();
    });

    document.addEventListener('cart:toggle', () => {
      this.checkCartDrawerState();
    });

    document.addEventListener('click', (e) => {
      if (e.target.matches('[data-cart-drawer-toggle], .cart-toggle, .cart-drawer-toggle, [href="#cart-drawer"]')) {
        setTimeout(() => this.checkCartDrawerState(), 100);
      }
    });

    this.setupCartDrawerObserver();
  }

  setupCartDrawerObserver() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (node.querySelector && node.querySelector('.cart-recommendations')) {
                this.onCartDrawerOpen();
              }
              else if (node.matches && (node.matches('.cart-drawer') || node.matches('[data-cart-drawer]'))) {
                setTimeout(() => this.checkCartDrawerState(), 100);
              }
            }
          });
        }
        
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const target = mutation.target;
          if (target.matches('.cart-drawer, [data-cart-drawer]')) {
            this.checkCartDrawerState();
          }
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'aria-hidden', 'data-cart-drawer']
    });
  }

  checkCartDrawerState() {
    const cartDrawer = document.querySelector('.cart-drawer, #cart-drawer, [data-cart-drawer]');
    if (!cartDrawer) {
      return;
    }

    const isOpen = this.isCartDrawerOpenNow(cartDrawer);
    
    if (isOpen && !this.isCartDrawerOpen) {
      this.onCartDrawerOpen();
    } else if (!isOpen && this.isCartDrawerOpen) {
      this.isCartDrawerOpen = false;
    }
  }

  isCartDrawerOpenNow(cartDrawer) {
    return (
      cartDrawer.classList.contains('open') || 
      cartDrawer.classList.contains('active') || 
      cartDrawer.classList.contains('is-open') ||
      cartDrawer.classList.contains('cart-drawer--open') ||
      cartDrawer.getAttribute('aria-hidden') === 'false' ||
      (getComputedStyle(cartDrawer).display !== 'none' && 
       getComputedStyle(cartDrawer).visibility !== 'hidden' &&
       getComputedStyle(cartDrawer).opacity !== '0')
    );
  }

  onCartDrawerOpen() {
    this.isCartDrawerOpen = true;
    
    setTimeout(() => {
      this.initialize();
    }, 150);
  }

  async initialize() {
    this.cartRecommendationsContainer = document.querySelector('[data-cart-recommendations]');
    this.cartItemsContainer = document.querySelector('#cartItemsHolder, .cart-drawer__items, .cart__items, [data-items-holder]');
    
    if (!this.cartRecommendationsContainer) {
      return;
    }

    // Get market information from data attributes
    this.isAustralianMarket = this.cartRecommendationsContainer.getAttribute('data-is-australian-market') === 'true';
    this.isEuropeanMarket = this.cartRecommendationsContainer.getAttribute('data-is-european-market') === 'true';
    this.enableTailored = this.cartRecommendationsContainer.getAttribute('data-enable-tailored') === 'true';
    this.cartHasPreorderProducts = this.cartRecommendationsContainer.getAttribute('data-cart-has-preorder') === 'true';
    
    if (this.isAustralianMarket) {
      this.marketType = 'australia';
    } else if (this.isEuropeanMarket) {
      this.marketType = 'europe';
    } else {
      this.marketType = 'international';
    }
    
    // Get restriction collection information
    this.hasRestrictionCollection = this.cartRecommendationsContainer.getAttribute('data-has-restriction-collection') === 'true';
    this.restrictionCollectionHandle = this.cartRecommendationsContainer.getAttribute('data-restriction-collection');
    
    // Load restricted product IDs if restriction collection is configured
    if (this.hasRestrictionCollection && this.restrictionCollectionHandle) {
      await this.loadRestrictedProducts();
    }

    if (this.isInitialized) {
      await this.refresh();
      return;
    }

    this.storeOriginalRecommendations();
    await this.updateCurrentCartProductIds();
    this.setupCartObserver();
    this.setupCartEventListeners();
    this.filterRecommendations();
    this.isInitialized = true;
  }

  async loadRestrictedProducts() {
    try {
      const response = await fetch(`/collections/${this.restrictionCollectionHandle}/products.json?limit=250`);
      if (response.ok) {
        const data = await response.json();
        this.restrictedProductIds.clear();
        data.products.forEach(product => {
          this.restrictedProductIds.add(product.id.toString());
        });
      }
    } catch (error) {
      // Silently handle errors
    }
  }

  storeOriginalRecommendations() {
    const recommendationProducts = this.cartRecommendationsContainer.querySelectorAll('.cart-recommendation-product');
    
    
    
    this.originalRecommendations = Array.from(recommendationProducts).map((product, index) => {
      const form = product.querySelector('form[data-product-form]');
      const productIdInput = form ? form.querySelector('input[name="product-id"]') : null;
      const productId = productIdInput ? productIdInput.value : null;
      
      const dataProductId = product.getAttribute('data-product-id');
      const dataProductOrigin = product.getAttribute('data-product-origin');
      const isInCartAtRender = product.getAttribute('data-in-cart') === 'true';
      
      // Get product title for logging
      const titleElement = product.querySelector('.cart-recommendation-product__title');
      const productTitle = titleElement ? titleElement.textContent.trim() : 'Unknown';
      
      
      
      if (productId || dataProductId) {
        const finalProductId = productId || dataProductId;
        this.originalRecommendationProductIds.add(finalProductId);
      }
      
      return {
        element: product.cloneNode(true),
        productId: productId || dataProductId,
        wasInCartAtRender: isInCartAtRender
      };
    });
    
    
  }

  async updateCurrentCartProductIds() {
    this.previousCartProductIds = new Set(this.currentCartProductIds);
    this.currentCartProductIds.clear();
    
    // Removed hasRestrictedItems logic
    this.cartOriginType = null; // Reset cart origin type
    this.cartHasPreorderProducts = false; // Reset pre-order status

    // Simple approach: Use Shopify's cart API directly
    try {
      const response = await fetch('/cart.js');
      if (response.ok) {
        const cartData = await response.json();
        
        if (cartData.items && cartData.items.length > 0) {
          // Analyze cart contents for origin type and pre-order status
          const cartOrigins = new Set();
          
          // Process cart items for origin analysis (now async)
          const originPromises = cartData.items.map(async (item, index) => {
            // Add both product ID and variant ID to our set
            if (item.product_id) {
              this.currentCartProductIds.add(item.product_id.toString());
            }
            if (item.variant_id) {
              this.currentCartProductIds.add(item.variant_id.toString());
            }
            
            // Check for pre-order status
            const isPreorder = await this.checkProductIsPreorder(item);
            if (isPreorder) {
              this.cartHasPreorderProducts = true;
            }
            
            // Determine product origin from cart item data (now async)
            const productOrigin = await this.getProductOriginFromCartItem(item);
            
            return productOrigin;
          });
          
          // Wait for all origin determinations to complete
          const origins = await Promise.all(originPromises);
          origins.forEach(origin => cartOrigins.add(origin));
          
          // Determine overall cart origin type
          this.cartOriginType = this.determineCartOriginType(cartOrigins);
          
          
          // No need to check collection updates - using single collection with filtering
        }
      }
    } catch (error) {
      // Silently handle cart API errors
    }

    // Also process DOM cart items for recently removed feature data
    const cartItems = this.cartItemsContainer ? 
      this.cartItemsContainer.querySelectorAll('.cart__item[data-cart-item], .cart__item[data-item]') : 
      document.querySelectorAll('#cartItemsHolder .cart__item[data-cart-item], #cartItemsHolder .cart__item[data-item]');

    cartItems.forEach((item, index) => {
      const dataItem = item.getAttribute('data-item') || item.getAttribute('data-cart-item');
      if (dataItem) {
        const variantId = dataItem.split(':')[0];
        if (variantId) {
          // Store cart item data for recently removed feature
          this.storeCartItemData(item, variantId);
          // Removed restricted tags checking
        }
      }
    });

    await this.handleRemovedItems();
  }

  async checkProductIsPreorder(cartItem) {
    try {
      // Primary strategy: fetch product data to get tags
      if (cartItem.handle) {
        try {
          const response = await fetch(`/products/${cartItem.handle}.js`);
          if (response.ok) {
            const productData = await response.json();
            const tags = productData.tags || [];
            
            // Check for pre-order tags (case-insensitive)
            const tagsLower = tags.map(tag => tag.toLowerCase());
            
            if (tagsLower.includes('pre-order') || 
                tagsLower.includes('preorder') || 
                tagsLower.includes('pre_order')) {
              return true;
            }
          }
        } catch (fetchError) {
          // Silent fallback to title checking
        }
      }
      
      // Fallback: check titles if we can't get tags
      if (cartItem.product_title) {
        const title = cartItem.product_title.toLowerCase();
        if (title.includes('pre-order') || 
            title.includes('preorder') || 
            title.includes('pre order')) {
          return true;
        }
      }
      
      return false;
    } catch (error) {
      return false;
    }
  }

  async getProductOriginFromCartItem(cartItem) {
    try {
      // Primary strategy: fetch product data to get tags
      if (cartItem.handle) {
        try {
          const response = await fetch(`/products/${cartItem.handle}.js`);
          if (response.ok) {
            const productData = await response.json();
            const tags = productData.tags || [];
            
            // Check for exact tag matches (case-insensitive)
            const tagsLower = tags.map(tag => tag.toLowerCase());
            
            if (tagsLower.includes('au')) {
              return 'au-local';
            }
            if (tagsLower.includes('eu')) {
              return 'eu-local';
            }
            
            return 'international';
          }
        } catch (fetchError) {
          // Silent fallback to title checking
        }
      }
      
      // Fallback: check titles only if we can't get tags
      // Check product title for origin indicators
      if (cartItem.product_title) {
        const title = cartItem.product_title.toLowerCase();
        
        if (title.includes('(au)') || title.includes('(au)')) {
          return 'au-local';
        }
        if (title.includes('(eu)')) {
          return 'eu-local';
        }
      }
      
      // Check variant title for origin indicators  
      if (cartItem.variant_title) {
        const variantTitle = cartItem.variant_title.toLowerCase();
        
        if (variantTitle.includes('(au)') || variantTitle.includes('(au)')) {
          return 'au-local';
        }
        if (variantTitle.includes('(eu)')) {
          return 'eu-local';
        }
      }
      
      // Default to international
      return 'international';
    } catch (error) {
      return 'international';
    }
  }

  determineCartOriginType(cartOrigins) {
    // If cart is empty, return null
    if (cartOrigins.size === 0) {
      return null;
    }
    
    // If cart has mixed origins, prioritize local products
    // AU local takes priority over everything
    if (cartOrigins.has('au-local')) {
      return 'au-local';
    }
    
    // EU local takes priority over international
    if (cartOrigins.has('eu-local')) {
      return 'eu-local';
    }
    
    // Otherwise it's international
    return 'international';
  }

  storeCartItemData(cartItemElement, productId) {
    try {
      const titleElement = cartItemElement.querySelector('.cart__item__title a, .cart__item__title, .cart-item__title, .cart__item-name, .product-title, [data-cart-item-title]');
      const title = titleElement ? titleElement.textContent.trim() : 'Unknown Product';
      
      const imageElement = cartItemElement.querySelector('img');
      let imageUrl = '';
      if (imageElement) {
        imageUrl = imageElement.src || imageElement.getAttribute('data-src') || '';
      }
      
      const priceElement = cartItemElement.querySelector('.cart__item__price, .cart-item__price, .price, [data-cart-item-price]');
      let price = 0;
      if (priceElement) {
        const priceText = priceElement.textContent.trim();
        // Look for price patterns more thoroughly
        const priceMatch = priceText.match(/\$?(\d+(?:[,\.]\d{3})*(?:\.\d{2})?)/);
        if (priceMatch) {
          // Remove commas and convert to number
          const cleanPrice = priceMatch[1].replace(/,/g, '');
          const parsedPrice = parseFloat(cleanPrice);
          
          if (!isNaN(parsedPrice)) {
            // If it's less than 100, it's likely in dollars, convert to cents
            if (parsedPrice < 100) {
              price = Math.round(parsedPrice * 100);
            } else {
              // Already in cents
              price = Math.round(parsedPrice);
            }
          }
        }
      }
      
      const variantIdInput = cartItemElement.querySelector('input[name="id"], input[data-variant-id]');
      const variantId = variantIdInput ? variantIdInput.value : productId;
      
      const productData = {
        id: productId,
        title: title,
        price: price,
        compare_at_price: null,
        featured_image: imageUrl,
        images: imageUrl ? [imageUrl] : [],
        variantId: variantId,
        available: true
      };
      
      this.removedProductsData.set(productId, productData);
    } catch (error) {
      // Silently handle errors
    }
  }

  // Removed checkCartItemForRestrictedTags method - no longer hiding recommendations
  // Removed checkCollectionUpdate and related methods - using single collection with filtering

  isRemovedItemCompatibleWithCurrentContext(productData) {
    try {
      // Apply pre-order filtering first (same as regular recommendations)
      if (this.currentCartProductIds.size > 0) {
        const productIsPreorder = this.isProductPreorder(productData);
        
        if (this.cartHasPreorderProducts) {
          // Cart has pre-order: only show pre-order products
          if (!productIsPreorder) {
            return false;
          }
        } else {
          // Cart doesn't have pre-order: only show non-pre-order products
          if (productIsPreorder) {
            return false;
          }
        }
      }
      
      if (!this.enableTailored) {
        return true;
      }
      const productOrigin = this.getProductOrigin(productData);
      
      
      
      // Apply same filtering logic as regular recommendations
      return this.shouldShowProductForCurrentState(productOrigin);
      
    } catch (error) {
      console.log('❌ Error checking recently removed compatibility:', error);
      return false;
    }
  }

  async handleRemovedItems() {
    // Recently removed feature now works with all markets
    // Will check compatibility of removed items with current cart origin
    
    if (this.previousCartProductIds.size === 0) {
      return;
    }

    const removedProductIds = Array.from(this.previousCartProductIds).filter(id => 
      !this.currentCartProductIds.has(id)
    );

    if (removedProductIds.length > 0) {
      let addedNewRecommendations = false;
      
      for (const productId of removedProductIds) {
        if (this.addedFromRecommendations.has(productId)) {
          this.addedFromRecommendations.delete(productId);
          continue;
        }
        
        const existingDynamicIndex = this.dynamicRecommendations.findIndex(rec => 
          rec.productId === productId.toString()
        );
        if (existingDynamicIndex !== -1) {
          continue;
        }
        
        let isOriginallyRecommended = false;
        
        if (this.originalRecommendationProductIds.has(productId)) {
          isOriginallyRecommended = true;
        }
        
        if (!isOriginallyRecommended) {
          for (const originalRec of this.originalRecommendations) {
            const form = originalRec.element.querySelector('form[data-product-form]');
            if (form) {
              const variantIdInput = form.querySelector('input[name="id"]');
              const productIdInput = form.querySelector('input[name="product-id"]');
              
              if ((variantIdInput && variantIdInput.value === productId) ||
                  (productIdInput && productIdInput.value === productId)) {
                isOriginallyRecommended = true;
                break;
              }
            }
          }
        }
        
        if (!isOriginallyRecommended) {
          await this.addRemovedItemToRecommendations(productId);
          addedNewRecommendations = true;
        }
      }
      
      if (addedNewRecommendations) {
        this.filterRecommendations();
      }
    }
  }

  async addRemovedItemToRecommendations(productId) {
    try {
      const existingIndex = this.dynamicRecommendations.findIndex(rec => rec.productId === productId.toString());
      if (existingIndex !== -1) {
        return;
      }
      
      const storedProductData = this.removedProductsData.get(productId);
      
      if (!storedProductData) {
        return await this.addRemovedItemToRecommendationsViaAPI(productId);
      }
      
      // Check if removed product is compatible with current collection/market context
      const isCompatible = this.isRemovedItemCompatibleWithCurrentContext(storedProductData);
      if (!isCompatible) {
        return;
      }
      
      
      if (!storedProductData.id || !storedProductData.title || storedProductData.title === 'Unknown Product') {
        return;
      }
      
      const newRecommendation = this.createDynamicRecommendation(storedProductData);
      if (newRecommendation) {
        this.dynamicRecommendations.push(newRecommendation);
      }
      
      if (this.dynamicRecommendations.length > 3) {
        this.dynamicRecommendations = this.dynamicRecommendations.slice(-3);
      }
      
    } catch (error) {
      // Silently handle errors
    }
  }

  async addRemovedItemToRecommendationsViaAPI(productId) {
    try {
      const existingIndex = this.dynamicRecommendations.findIndex(rec => rec.productId === productId.toString());
      if (existingIndex !== -1) {
        return;
      }
      
      let response = await fetch(`/variants/${productId}.js`);
      let productData;
      
      if (response.ok) {
        const variantData = await response.json();
        
        productData = {
          id: variantData.product_id || variantData.id,
          title: variantData.name || variantData.product_title || variantData.title || 'Unknown Product',
          price: variantData.price || 0,
          compare_at_price: variantData.compare_at_price || null,
          featured_image: variantData.featured_image || variantData.image || variantData.product_image || null,
          images: variantData.product_images || variantData.images || (variantData.featured_image ? [variantData.featured_image] : []),
          variants: [variantData],
          available: variantData.available !== false
        };
        
        try {
          const searchResponse = await fetch(`/search/suggest.json?q=${encodeURIComponent(variantData.name)}&resources[type]=product&resources[limit]=1`);
          if (searchResponse.ok) {
            const searchData = await searchResponse.json();
            
            if (searchData.resources && searchData.resources.results && searchData.resources.results.products && searchData.resources.results.products.length > 0) {
              const productInfo = searchData.resources.results.products[0];
              
              productData.id = productInfo.id || productData.id;
              productData.title = productInfo.title || productData.title;
              productData.featured_image = productInfo.featured_image || productData.featured_image;
              productData.images = productInfo.images || productData.images;
              productData.tags = productInfo.tags || productData.tags;
            }
          }
        } catch (searchError) {
          // Silently handle search errors
        }
      } else {
        response = await fetch(`/products/${productId}.js`);
        if (!response.ok) {
          return;
        }
        productData = await response.json();
      }
      
      // Check if recently removed API product is compatible with current collection/market context
      const isCompatible = this.isRemovedItemCompatibleWithCurrentContext(productData);
      if (!isCompatible) {
        return;
      }
      
      
      if (!productData.id || !productData.title || productData.title === 'Unknown Product') {
        return;
      }
      
      const finalExistingIndex = this.dynamicRecommendations.findIndex(rec => rec.productId === productData.id.toString());
      if (finalExistingIndex !== -1) {
        return;
      }
      
      const newRecommendation = this.createDynamicRecommendation(productData);
      if (newRecommendation) {
        this.dynamicRecommendations.push(newRecommendation);
      }
      
      if (this.dynamicRecommendations.length > 3) {
        this.dynamicRecommendations = this.dynamicRecommendations.slice(-3);
      }
      
    } catch (error) {
      // Silently handle errors
    }
  }

  createDynamicRecommendation(productData) {
    // Skip free products
    if (productData.price !== undefined && productData.price === 0) {
      return null;
    }
    
    const variants = productData.variants || [];
    const hasMultipleVariants = variants.length > 1;
    let imageUrl = '';
    
    try {
      if (productData.featured_image) {
        if (typeof productData.featured_image === 'string') {
          imageUrl = productData.featured_image;
        } else if (productData.featured_image.src) {
          imageUrl = productData.featured_image.src;
        }
      } else if (productData.images && productData.images.length > 0) {
        const firstImage = productData.images[0];
        if (typeof firstImage === 'string') {
          imageUrl = firstImage;
        } else if (firstImage && firstImage.src) {
          imageUrl = firstImage.src;
        }
      }
      
      if (imageUrl && !imageUrl.includes('data:image')) {
        if (!imageUrl.includes('?') && !imageUrl.includes('_120x120')) {
          imageUrl = imageUrl.includes('//') ? `${imageUrl}?v=120x120&crop=center` : imageUrl;
        }
      } else {
        imageUrl = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEyMCIgdmlld0JveD0iMCAwIDEyMCAxMjAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEyMCIgaGVpZ2h0PSIxMjAiIGZpbGw9IiNmNWY1ZjUiLz48L3N2Zz4=';
      }
    } catch (imageError) {
      imageUrl = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEyMCIgdmlld0JveD0iMCAwIDEyMCAxMjAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEyMCIgaGVpZ2h0PSIxMjAiIGZpbGw9IiNmNWY1ZjUiLz48L3N2Zz4=';
    }
    
    const defaultVariant = variants[0] || {};
    const variantId = defaultVariant.id || productData.variantId || productData.id;
    
    if (!variantId) {
      return null;
    }

    // Ensure we have valid price data
    let defaultPrice = defaultVariant.price || productData.price || 0;
    let defaultComparePrice = defaultVariant.compare_at_price || productData.compare_at_price || null;
    
    // Convert prices to cents if they appear to be in dollars
    if (defaultPrice > 0 && defaultPrice < 100 && defaultPrice % 1 !== 0) {
      defaultPrice = Math.round(defaultPrice * 100);
    }
    if (defaultComparePrice && defaultComparePrice > 0 && defaultComparePrice < 100 && defaultComparePrice % 1 !== 0) {
      defaultComparePrice = Math.round(defaultComparePrice * 100);
    }

    const variantOptions = variants.filter(v => v.available).map(v => {
      let vPrice = v.price || defaultPrice;
      let vComparePrice = v.compare_at_price || null;
      
      // Convert to cents if needed
      if (vPrice > 0 && vPrice < 100 && vPrice % 1 !== 0) {
        vPrice = Math.round(vPrice * 100);
      }
      if (vComparePrice && vComparePrice > 0 && vComparePrice < 100 && vComparePrice % 1 !== 0) {
        vComparePrice = Math.round(vComparePrice * 100);
      }
      
      return {
        id: v.id,
        title: v.title,
        price: vPrice,
        compare_at_price: vComparePrice,
        available: v.available
      };
    });
    
    const uniqueId = `dyn-${productData.id}-${Date.now()}`;
    
    const recommendationHtml = `
      <div class="cart-recommendation-product cart-recommendation-product--dynamic" data-product-id="${productData.id}">
        <div class="cart-recommendation-product__top">
          <div class="cart-recommendation-product__image">
            <img src="${imageUrl}" alt="${productData.title.replace(/"/g, '&quot;')}" width="120" height="120" onerror="this.src='data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEyMCIgdmlld0JveD0iMCAwIDEyMCAxMjAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEyMCIgaGVpZ2h0PSIxMjAiIGZpbGw9IiNmNWY1ZjUiLz48L3N2Zz4=';">
            ${(defaultComparePrice && defaultComparePrice > defaultPrice) ? '<span class="cart-recommendation-product__badge">Sale</span>' : ''}
          </div>
          
                      <div class="cart-recommendation-product__info">
              <h4 class="cart-recommendation-product__title">${productData.title.replace(/"/g, '&quot;')}</h4>
              
              <div class="cart-recommendation-product__removed-note">Recently removed</div>
            </div>
          
          <form method="post" action="/cart/add" id="${uniqueId}" class="quick__form" data-product-form data-quickbuy-form>
            ${!hasMultipleVariants ? `<input type="hidden" name="id" value="${variantId}">` : ''}
            
            <input type="hidden" name="quantity" value="1">
            <input type="hidden" name="product-id" value="${productData.id}">
            
            <button type="submit" 
              name="add"
              class="cart-recommendation-product__add-btn"
              data-add-to-cart
              data-atc-trigger
              data-upsell-add
              aria-label="Add ${productData.title.replace(/"/g, '&quot;')} to cart">
              add
            </button>
            
            <div class="product-grid-item__error" role="alert" data-cart-errors-container></div>
          </form>
        </div>
        
        ${hasMultipleVariants ? `
          <div class="cart-recommendation-product__variants">
            <select name="id" 
              class="cart-recommendation-product__variant-select" 
              data-variant-select
              aria-label="Select ${productData.title.replace(/"/g, '&quot;')} variant"
              form="${uniqueId}">
              ${variantOptions.map(v => `
                <option value="${v.id}" 
                  data-price="${v.price}"
                  data-compare-price="${v.compare_at_price || ''}"
                  ${v.id === variantId ? 'selected' : ''}>
                  ${v.title}
                </option>
              `).join('')}
            </select>
          </div>
        ` : ''}
      </div>
    `;
    
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = recommendationHtml;
    const element = tempDiv.firstElementChild;
    
    // No price change handler needed since we don't show prices for recently removed items
    
    return {
      element: element,
      productId: productData.id.toString(),
      isDynamic: true,
      title: productData.title,
      price: defaultPrice // Store price for filtering
    };
  }

  formatMoney(cents) {
    // Handle various input types and ensure we have a valid number
    let amount = cents;
    
    // If it's already a decimal (dollars), convert to cents
    if (typeof amount === 'string') {
      amount = parseFloat(amount);
    }
    
    // If amount is NaN, null, undefined, or not a number, default to 0
    if (isNaN(amount) || amount === null || amount === undefined) {
      amount = 0;
    }
    
    // If the amount seems to be in dollars (less than 100), convert to cents
    // This handles cases where the API returns prices in dollars instead of cents
    if (amount > 0 && amount < 100 && amount % 1 !== 0) {
      amount = amount * 100;
    }
    
    const dollars = (amount / 100).toFixed(2);
    return `$${dollars}`;
  }

  filterRecommendations() {
    if (!this.cartRecommendationsContainer || this.originalRecommendations.length === 0) {
      return;
    }

    // Check if any restricted products are in the cart
    if (this.hasRestrictionCollection && this.restrictedProductIds.size > 0) {
      const hasRestrictedInCart = Array.from(this.currentCartProductIds).some(id => 
        this.restrictedProductIds.has(id)
      );
      
      if (hasRestrictedInCart) {
        // Hide the entire recommendations section
        this.cartRecommendationsContainer.style.display = 'none';
        return;
      } else {
        // Show the section if it was previously hidden
        this.cartRecommendationsContainer.style.display = '';
      }
    }
  
    const productsContainer = this.cartRecommendationsContainer.querySelector('.cart-recommendations__products');
    if (!productsContainer) {
      return;
    }
  
    productsContainer.innerHTML = '';
  
    const dynamicRecommendationProductIds = new Set();
    this.dynamicRecommendations.forEach(rec => {
      if (rec.productId) {
        dynamicRecommendationProductIds.add(rec.productId.toString());
      }
    });
    
    const availableOriginalRecommendations = [];
    const availableDynamicRecommendations = [];
    const minRecommendations = 4;
    const maxTotalProducts = 5;
    
    this.originalRecommendations.forEach((recommendation, index) => {
      const productId = recommendation.productId;
      const isInCart = this.isProductInCart(productId, recommendation.element);
      const hasDynamicVersion = dynamicRecommendationProductIds.has(productId.toString());
      
      // For static recommendations, check compatibility based on market and cart state
      const productData = this.getProductDataFromElement(recommendation.element);
      const isCompatible = this.isProductCompatibleWithCart(productData);
      
      
      if (!isInCart && !hasDynamicVersion && isCompatible) {
        availableOriginalRecommendations.push(recommendation);
      }
    });
    
    this.dynamicRecommendations.forEach((recommendation, index) => {
      const productId = recommendation.productId;
      const isInCart = this.isProductInCart(productId, recommendation.element);
      
      // Check compatibility for dynamic recommendations too
      // Note: Dynamic recommendations (recently removed items) will need to fetch tags
      // For now, we'll rely on title checking for pre-order status
      const isCompatible = this.isProductCompatibleWithCart({
        title: recommendation.title || '',
        id: recommendation.productId,
        tags: [], // Dynamic recommendations will rely on title
        price: recommendation.price, // Include price for filtering
        isPreorder: false // Will be determined by isProductPreorder method via title check
      });
      
      if (!isInCart && isCompatible) {
        availableDynamicRecommendations.push(recommendation);
      }
    });
  
    let addedCount = 0;
    const totalAvailable = availableOriginalRecommendations.length + availableDynamicRecommendations.length;
    
    if (totalAvailable < minRecommendations) {
      this.originalRecommendations.forEach((recommendation) => {
        if (addedCount >= minRecommendations) return;
        
        const productId = recommendation.productId;
        const isCurrentlyInCart = this.isProductInCart(productId, recommendation.element);
        const hasDynamicVersion = dynamicRecommendationProductIds.has(productId.toString());
        
        // Check compatibility for fallback recommendations too
        const productData = this.getProductDataFromElement(recommendation.element);
        const isCompatible = this.isProductCompatibleWithCart(productData);
        
        
        if (!isCurrentlyInCart && !hasDynamicVersion && isCompatible && !availableOriginalRecommendations.some(r => r.productId === productId)) {
          availableOriginalRecommendations.push(recommendation);
        }
      });
    }
    
    availableDynamicRecommendations.forEach((recommendation, index) => {
      if (addedCount >= maxTotalProducts) return;
      
      const clonedElement = recommendation.element.cloneNode(true);
      clonedElement.style.animationDelay = `${0.3 + (addedCount * 0.1)}s`;
      productsContainer.appendChild(clonedElement);
      addedCount++;
    });
    
    const remainingNeeded = Math.max(minRecommendations - addedCount, 0);
    const totalToAdd = Math.min(availableOriginalRecommendations.length, maxTotalProducts - addedCount, Math.max(remainingNeeded, maxTotalProducts - addedCount));
    
    availableOriginalRecommendations.slice(0, totalToAdd).forEach((recommendation, index) => {
      const clonedElement = recommendation.element.cloneNode(true);
      clonedElement.style.animationDelay = `${0.3 + (addedCount * 0.1)}s`;
      productsContainer.appendChild(clonedElement);
      addedCount++;
    });
    
    // Show recommendations container if we have products, otherwise hide it
    if (addedCount > 0) {
      this.cartRecommendationsContainer.style.display = '';
      
      // Fade in the recommendations after filtering is complete
      setTimeout(() => {
        this.cartRecommendationsContainer.style.opacity = '1';
      }, 50);
    } else {
      // Hide container if no products match filtering criteria
      this.cartRecommendationsContainer.style.display = 'none';
    }
  }

  shouldShowProductForCurrentState(productOrigin) {
    // Determine filtering based on market and cart contents
    
    // Empty cart: filter by market
    if (!this.cartOriginType) {
      let shouldShow = false;
      
      switch (this.marketType) {
        case 'australia':
          // AU market empty cart: show only AU products
          shouldShow = productOrigin === 'au-local';
          return shouldShow;
        
        case 'europe':
          // EU market empty cart: show only EU products
          shouldShow = productOrigin === 'eu-local';
          return shouldShow;
        
        default:
          // International market empty cart: show international products
          shouldShow = productOrigin === 'international';
          return shouldShow;
      }
    }
    
    // Cart has items: filter by cart contents
    let shouldShow = false;
    
    switch (this.cartOriginType) {
      case 'au-local':
        // AU cart: Allow only AU products
        shouldShow = productOrigin === 'au-local';
        return shouldShow;
        
      case 'eu-local':
        // EU cart: Allow only EU products
        shouldShow = productOrigin === 'eu-local';
        return shouldShow;
        
      case 'international':
        // International cart: Allow only international products
        shouldShow = productOrigin === 'international';
        return shouldShow;
        
      default:
        // Fallback: allow all products
        return true;
    }
  }

  isProductCompatibleWithCart(productData) {
    // Skip free products
    if (productData.price !== undefined && productData.price === 0) {
      return false;
    }
    
    // Pre-order filtering: If cart has pre-order products, only show pre-order recommendations
    // If cart doesn't have pre-order products, only show non-pre-order recommendations
    if (this.currentCartProductIds.size > 0) {
      const productIsPreorder = productData.isPreorder !== undefined 
        ? productData.isPreorder 
        : this.isProductPreorder(productData);
      
      if (this.cartHasPreorderProducts) {
        // Cart has pre-order: only show pre-order products
        if (!productIsPreorder) {
          return false;
        }
      } else {
        // Cart doesn't have pre-order: only show non-pre-order products
        if (productIsPreorder) {
          return false;
        }
      }
    }
    // Empty cart: no pre-order filtering (keep current behavior)
    
    if (!this.enableTailored) {
      return true;
    }
    // Get the origin of the recommendation product
    const productOrigin = productData.origin || this.getProductOrigin(productData) || 'international';
    
    
    
    const isCompatible = this.shouldShowProductForCurrentState(productOrigin);
    
    
    return isCompatible;
  }

  getProductDataFromElement(productElement) {
    try {
      // Extract product data from DOM element for compatibility checking
      const titleElement = productElement.querySelector('.cart-recommendation-product__title');
      const title = titleElement ? titleElement.textContent.trim() : '';
      
      // Get product handle for tag fetching
      const productLinkElement = productElement.querySelector('a[href*="/products/"]');
      const productHandle = productLinkElement ? 
        productLinkElement.getAttribute('href').split('/products/')[1].split('?')[0] : null;
      
      // Try to get pre-calculated origin from Liquid template
      const productId = productElement.getAttribute('data-product-id');
      const productOrigin = productElement.getAttribute('data-product-origin');
      const isPreorder = productElement.getAttribute('data-is-preorder') === 'true';
      
      // Extract price from the price element
      let price = undefined;
      const priceElement = productElement.querySelector('.cart-recommendation-product__price, .cart-recommendation-product__price--sale');
      if (priceElement) {
        const priceText = priceElement.textContent.trim();
        const priceMatch = priceText.match(/\$?(\d+(?:[,\.]\d{3})*(?:\.\d{2})?)/);
        if (priceMatch) {
          const cleanPrice = priceMatch[1].replace(/,/g, '');
          const parsedPrice = parseFloat(cleanPrice);
          if (!isNaN(parsedPrice)) {
            // Convert to cents if in dollars
            price = parsedPrice < 100 && parsedPrice % 1 !== 0 ? Math.round(parsedPrice * 100) : Math.round(parsedPrice);
          }
        }
      }
      
      return {
        title: title,
        id: productId,
        handle: productHandle, // Add handle for tag fetching
        tags: [], // Will be fetched if needed
        origin: productOrigin, // Use pre-calculated origin from Liquid if available
        price: price, // Add price for filtering
        isPreorder: isPreorder // Use pre-calculated pre-order status from Liquid if available
      };
    } catch (error) {
      return {
        title: '',
        id: null,
        handle: null,
        tags: [],
        origin: null,
        price: undefined,
        isPreorder: false
      };
    }
  }

  isProductInCart(productId, recommendationElement) {
    // Normalize productId to string for consistent comparison
    const normalizedProductId = productId ? productId.toString() : '';
    
    if (!normalizedProductId) {
      return false;
    }
    
    // Simple check: is this product ID in our cart?
    const isInCart = this.currentCartProductIds.has(normalizedProductId);
    
    return isInCart;
  }

  setupCartObserver() {
    if (!this.cartItemsContainer) {
      return;
    }

    const observer = new MutationObserver((mutations) => {
      let cartChanged = false;
      
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          const target = mutation.target;
          
          if (target === this.cartItemsContainer || 
              target.closest('#cartItemsHolder, .cart-drawer__items, .cart__items')) {
            cartChanged = true;
          }
        }
        
        if (mutation.type === 'attributes' && 
            (mutation.attributeName === 'data-item' || mutation.attributeName === 'data-cart-item')) {
          cartChanged = true;
        }
      });
      
      if (cartChanged) {
        setTimeout(async () => {
          await this.updateCurrentCartProductIds();
          this.filterRecommendations();
        }, 100);
      }
    });

    observer.observe(this.cartItemsContainer, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-item', 'data-cart-item']
    });
  }

  setupCartEventListeners() {
    const cartEvents = ['cart:updated', 'cart:item-added', 'cart:item-removed', 'cart:quantity-changed', 'cart:open'];
    cartEvents.forEach(eventName => {
      document.addEventListener(eventName, () => {
        if (this.isCartDrawerOpen && this.isInitialized) {
          setTimeout(async () => {
            await this.updateCurrentCartProductIds();
            this.filterRecommendations();
          }, 100);
        }
      });
    });

    // Listen for form submissions from recommendation products
    document.addEventListener('submit', (e) => {
      if (!this.isCartDrawerOpen || !this.isInitialized) return;
      
      const form = e.target;
      if (form.closest('.cart-recommendations') && form.matches('[data-product-form], [data-quickbuy-form]')) {
        
        const productIdInput = form.querySelector('input[name="product-id"]');
        const variantIdInput = form.querySelector('input[name="id"]') || form.querySelector('select[name="id"]');
        
        if (productIdInput && productIdInput.value) {
          this.addedFromRecommendations.add(productIdInput.value);
        }
        if (variantIdInput) {
          const variantId = variantIdInput.value || variantIdInput.options?.[variantIdInput.selectedIndex]?.value;
          if (variantId) {
            this.addedFromRecommendations.add(variantId);
          }
        }
        
        // Force update after a reasonable delay to allow cart to update
        setTimeout(async () => {
          await this.updateCurrentCartProductIds();
          this.filterRecommendations();
        }, 1000);
      }
    });

    document.addEventListener('click', (e) => {
      if (!this.isCartDrawerOpen || !this.isInitialized) return;
      
      if (e.target.matches('[data-quantity-button], [data-item-remove], [data-quantity-minus], [data-quantity-plus]')) {
        setTimeout(async () => {
          await this.updateCurrentCartProductIds();
          this.filterRecommendations();
        }, 200);
      }
      
      if (e.target.closest('.cart-recommendations') && 
          e.target.matches('[data-add-to-cart], button[type="submit"]')) {
        const form = e.target.closest('form');
        if (form) {
          const productIdInput = form.querySelector('input[name="product-id"]');
          const variantIdInput = form.querySelector('input[name="id"]') || form.querySelector('select[name="id"]');
          
          if (productIdInput && productIdInput.value) {
            this.addedFromRecommendations.add(productIdInput.value);
          }
          if (variantIdInput) {
            const variantId = variantIdInput.value || variantIdInput.options?.[variantIdInput.selectedIndex]?.value;
            if (variantId) {
              this.addedFromRecommendations.add(variantId);
            }
          }
        }
        
        setTimeout(async () => {
          await this.updateCurrentCartProductIds();
          this.filterRecommendations();
        }, 800);
      }
    });

    document.addEventListener('change', (e) => {
      if (!this.isCartDrawerOpen || !this.isInitialized) return;
      
      if (e.target.matches('[data-quantity-field]')) {
        setTimeout(async () => {
          await this.updateCurrentCartProductIds();
          this.filterRecommendations();
        }, 200);
      }
    });

    // Additional listener for Shopify's cart.js events (if using cart.js)
    if (window.CartJS) {
      CartJS.on('cart.requestComplete', () => {
        if (this.isCartDrawerOpen && this.isInitialized) {
          setTimeout(async () => {
            await this.updateCurrentCartProductIds();
            this.filterRecommendations();
          }, 100);
        }
      });
    }
  }

  async refresh() {
    if (this.isInitialized && this.isCartDrawerOpen) {
      await this.updateCurrentCartProductIds();
      this.filterRecommendations();
    }
  }

  forceInitialize() {
    this.isInitialized = false;
    this.onCartDrawerOpen();
  }



  isProductPreorder(productData) {
    try {
      // Use pre-calculated pre-order status if available (from Liquid template)
      if (productData.isPreorder !== undefined) {
        return productData.isPreorder;
      }
      
      // Check tags if available
      if (productData.tags && Array.isArray(productData.tags)) {
        const tagsLower = productData.tags.map(tag => tag.toLowerCase());
        if (tagsLower.includes('pre-order') || 
            tagsLower.includes('preorder') || 
            tagsLower.includes('pre_order')) {
          return true;
        }
      }
      
      // Fallback: Check title for pre-order indicators
      if (productData.title) {
        const title = productData.title.toLowerCase();
        if (title.includes('pre-order') || 
            title.includes('preorder') || 
            title.includes('pre order')) {
          return true;
        }
      }
      
      return false;
    } catch (error) {
      return false;
    }
  }

  getProductOrigin(productData) {
    try {
      // Use pre-calculated origin if available (from Liquid template - this should be the primary source)
      if (productData.origin) {
        
        return productData.origin;
      }
      
      // Fallback: Check title for origin indicators
      if (productData.title) {
        const title = productData.title.toLowerCase();
        
        
        if (title.includes('(au)') || title.includes('(au)')) {
          
          return 'au-local';
        }
        if (title.includes('(eu)')) {
          
          return 'eu-local';
        }
      }
      
      // Default to international if no local indicators found
      
      return 'international';
    } catch (error) {
      return 'international';
    }
  }

}

window.cartRecommendations = new CartRecommendations();